import { z } from 'zod';
import { IsoTimestampSchema, UuidSchema } from './common';

export const CONVERSATION_VERDICTS = ['pass', 'partial', 'fail', 'na'] as const;
export const ConversationVerdictSchema = z.enum(CONVERSATION_VERDICTS);
export type ConversationVerdict = z.infer<typeof ConversationVerdictSchema>;

// A single turn of a real conversation. role is left free (customer | agent |
// context | tool_call | tool_result | …) so emitters are not boxed in; name
// carries the tool for tool_call / tool_result turns.
export const ConversationTurnSchema = z.object({
  role: z.string().min(1).max(32),
  text: z.string().max(20000),
  name: z.string().max(128).optional(),
  at: z.string().max(64).optional(),
});
export type ConversationTurn = z.infer<typeof ConversationTurnSchema>;

// --- Ingest (from the application, e.g. VERA; auth = application token) ---

export const ConversationIngestSchema = z.object({
  // Optional session id. When present, re-sending the same id updates the row
  // instead of duplicating it.
  externalId: z.string().min(1).max(128).optional(),
  channel: z.string().max(64).optional(),
  startedAt: IsoTimestampSchema.optional(),
  endedAt: IsoTimestampSchema.optional(),
  outcome: z.string().max(128).optional(),
  // When absent it is derived server-side from the transcript.
  turns: z.number().int().nonnegative().optional(),
  inputTokens: z.number().int().nonnegative().default(0),
  outputTokens: z.number().int().nonnegative().default(0),
  productCostUsd: z.number().nonnegative().default(0),
  transcript: z.array(ConversationTurnSchema).max(500).default([]),
  metadata: z.record(z.unknown()).optional(),
});
export type ConversationIngest = z.infer<typeof ConversationIngestSchema>;

export const ConversationBatchIngestSchema = z.object({
  conversations: z.array(ConversationIngestSchema).min(1).max(100),
});
export type ConversationBatchIngest = z.infer<typeof ConversationBatchIngestSchema>;

export const ConversationIngestResponseSchema = z.object({
  id: UuidSchema,
});
export type ConversationIngestResponse = z.infer<typeof ConversationIngestResponseSchema>;

export const ConversationBatchIngestResponseSchema = z.object({
  ids: z.array(UuidSchema),
});
export type ConversationBatchIngestResponse = z.infer<typeof ConversationBatchIngestResponseSchema>;

// --- Read (admin panel) ---

export const ConversationSummarySchema = z.object({
  id: UuidSchema,
  applicationId: UuidSchema,
  applicationName: z.string().nullable(),
  externalId: z.string().nullable(),
  channel: z.string().nullable(),
  outcome: z.string().nullable(),
  turns: z.number().int().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  productCostUsd: z.number().nonnegative(),
  startedAt: IsoTimestampSchema.nullable(),
  endedAt: IsoTimestampSchema.nullable(),
  createdAt: IsoTimestampSchema,
  // Latest judge result (null verdict = not judged yet).
  verdict: ConversationVerdictSchema.nullable(),
  score: z.number().int().nullable(),
  reasoning: z.string().nullable(),
  issues: z.array(z.string()),
  judgeModel: z.string().nullable(),
  judgedAt: IsoTimestampSchema.nullable(),
  judgeCostUsd: z.number().nonnegative().nullable(),
});
export type ConversationSummary = z.infer<typeof ConversationSummarySchema>;

export const ConversationDetailSchema = ConversationSummarySchema.extend({
  transcript: z.array(ConversationTurnSchema),
  metadata: z.record(z.unknown()),
});
export type ConversationDetail = z.infer<typeof ConversationDetailSchema>;

export const ConversationListResponseSchema = z.object({
  items: z.array(ConversationSummarySchema),
});
export type ConversationListResponse = z.infer<typeof ConversationListResponseSchema>;
